import { CurrencyInput, PercentInput } from '@client/components/NumericInput';

interface AccountDetailFormState {
  currentValue: number | null;
  futureContributions: number | null;
  expectedReturn: number | null;
  capitalGainsTaxRate: number | null;
}

type AccountDetailFormErrors = Partial<Record<keyof AccountDetailFormState, string>>;

interface AccountDetailFieldsProps {
  formState: AccountDetailFormState;
  formErrors?: AccountDetailFormErrors;
  onChangeField: (field: keyof AccountDetailFormState) => (value: number | null) => void;
}

const ErrorMessage = ({ message }: { message?: string }) => {
  if (!message) {
    return null;
  }
  return <p className="ml-2 text-sm text-red-400">{message}</p>;
};

interface AccountDetailFieldProps {
  title: string;
  description: string | React.ReactNode;
  children: React.ReactNode;
}

function AccountDetailField({ title, description, children }: AccountDetailFieldProps) {
  return (
    <div className="col-span-1 flex flex-col gap-2">
      <h3 className="text-base font-medium leading-5 text-gray-800">{title}</h3>
      <p className="text-sm text-gray-700">{description}</p>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export function AccountDetailFields({
  formState,
  formErrors = {},
  onChangeField,
}: AccountDetailFieldsProps) {
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* Current Value - Required */}
      <AccountDetailField
        title="Current Value"
        description="Present-day value of the asset that can be reserved for future long-term care expenses"
      >
        <CurrencyInput
          label="CURRENT VALUE OF ASSET"
          placeholder="Enter dollars"
          value={formState.currentValue}
          onChange={onChangeField('currentValue')}
          onPreview={onChangeField('currentValue')}
        />
        <ErrorMessage message={formErrors.currentValue} />
      </AccountDetailField>

      {/* Monthly Contribution - Optional */}
      <AccountDetailField
        title="Monthly Contribution"
        description="Money added each month to increase the total value available for LTC expenses"
      >
        <CurrencyInput
          label="MONTHLY CONTRIBUTION"
          placeholder="Enter dollars"
          value={formState.futureContributions}
          onChange={onChangeField('futureContributions')}
          onPreview={onChangeField('futureContributions')}
        />
      </AccountDetailField>

      {/* Expected Rate of Return - Required */}
      <AccountDetailField
        title="Annual Rate of Return"
        description="The expected annual rate of return applied to both existing assets and monthly contributions"
      >
        <PercentInput
          label="ANNUAL RATE OF RETURN"
          placeholder="Enter percent"
          value={formState.expectedReturn}
          onChange={onChangeField('expectedReturn')}
          onPreview={onChangeField('expectedReturn')}
        />
        <ErrorMessage message={formErrors.expectedReturn} />
      </AccountDetailField>

      {/* Capital Gains Tax Rate - Required */}
      <AccountDetailField
        title="Gains Tax Rate"
        description={
          <>
            The tax rate applied to profits from selling investments. For more info, view this{' '}
            <a
              href="https://www.irs.gov/taxtopics/tc409"
              target="_blank"
              rel="noreferrer"
              className="text-purple underline"
            >
              IRS article
            </a>
            .
          </>
        }
      >
        <PercentInput
          label="GAINS TAX RATE"
          placeholder="Enter percent"
          value={formState.capitalGainsTaxRate}
          onChange={onChangeField('capitalGainsTaxRate')}
          onPreview={onChangeField('capitalGainsTaxRate')}
        />
        <ErrorMessage message={formErrors.capitalGainsTaxRate} />
      </AccountDetailField>
    </div>
  );
}

